import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { DimensionScores } from '@/types'

interface DimensionBarChartProps {
  dimensions: DimensionScores
  height?: number
  showWeights?: boolean
  className?: string
}

const DIMENSION_COLORS = {
  legal: '#10b981',     // green-500
  financial: '#3b82f6', // blue-500
  operational: '#f59e0b', // amber-500
  market: '#8b5cf6'     // violet-500
}

export function DimensionBarChart({ 
  dimensions, 
  height = 300,
  showWeights = false,
  className 
}: DimensionBarChartProps) {
  const data = [
    {
      name: 'Legal',
      score: dimensions.legal.score,
      weight: dimensions.legal.weight * 100,
      color: DIMENSION_COLORS.legal
    },
    {
      name: 'Financial',
      score: dimensions.financial.score,
      weight: dimensions.financial.weight * 100,
      color: DIMENSION_COLORS.financial
    },
    {
      name: 'Operational',
      score: dimensions.operational.score,
      weight: dimensions.operational.weight * 100,
      color: DIMENSION_COLORS.operational
    },
    {
      name: 'Market',
      score: dimensions.market.score,
      weight: dimensions.market.weight * 100,
      color: DIMENSION_COLORS.market
    }
  ]

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 dark:text-white">{label}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Score: <span className="font-medium">{data.score}</span>
          </p>
          {showWeights && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Weight: <span className="font-medium">{data.weight.toFixed(0)}%</span>
            </p>
          )}
        </div>
      )
    }
    return null
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e5e7eb"
            className="dark:stroke-gray-600"
          />
          <XAxis 
            dataKey="name" 
            tick={{ 
              fontSize: 12, 
              fill: '#6b7280' 
            }}
            className="dark:fill-gray-400"
          />
          <YAxis 
            domain={[0, 100]}
            tick={{ 
              fontSize: 12, 
              fill: '#6b7280' 
            }}
            className="dark:fill-gray-400"
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="score" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}